import { useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import { toast } from 'react-hot-toast';
import InitialContactInfoDrawer from '../drawers/InitialContactInfoDrawer';
import { referringProfessionalName } from '../ReferringProfessionalField';
import PrimaryContactInfoDrawer from '../drawers/PrimaryContactInfoDrawer';
import CampaignRequestClientInfoDrawer from '../drawers/CampaignRequestClientInfoDrawer';

interface Props {
    campaignRequest: any;
    patient: any;
    metadata: any;
    application?: any;
    onUpdate?: (apiResponse: any) => void;
}

const RequestOverview: React.FC<Props> = ({
    campaignRequest,
    patient,
    metadata,
    application,
    onUpdate,
}) => {
    const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false);
    const [isPrimaryContactDrawerOpen, setIsPrimaryContactDrawerOpen] =
        useState(false);
    const [isClientDrawerOpen, setIsClientDrawerOpen] = useState(false);
    const requestPatient = campaignRequest || {};

    // Check if this campaign request is for 'self'
    const isCampaignForSelf = campaignRequest?.campaign_is_for?.code === 'self';

    // Check if this campaign request has been approved (has a campaign or application)
    const isApproved = !!(campaignRequest?.campaign || application);

    // Helper functions
    const formatState = (stateValue: any): string => {
        if (!stateValue) return '';
        if (typeof stateValue === 'string') return stateValue;
        return (
            stateValue?.state ||
            stateValue?.abbreviation ||
            stateValue?.st ||
            ''
        );
    };

    const formatDate = (dateString?: string | null): string => {
        if (!dateString) return '';
        try {
            const dateStr = String(dateString);
            const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const [, year, month, day] = match;
                const date = new Date(
                    parseInt(year),
                    parseInt(month) - 1,
                    parseInt(day),
                );
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });
            }
            return '';
        } catch {
            return '';
        }
    };

    // Primary Contact Information
    const requestSource = campaignRequest?.request_source
        ? campaignRequest.request_source
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (char: string) => char.toUpperCase())
        : '';

    let initialEmail;
    if (campaignRequest?.initial_email) {
        const mailTo = `mailto:${campaignRequest.initial_email}`;
        initialEmail = (
            <a className='text-primary' href={mailTo}>
                {campaignRequest.initial_email}
            </a>
        );
    } else {
        initialEmail = '';
    }

    let communityTypes;
    if (
        campaignRequest?.community_types &&
        campaignRequest.community_types.length > 0
    ) {
        communityTypes = campaignRequest.community_types.map(
            (communityType: any) => (
                <li key={communityType.id}>{communityType.name}</li>
            ),
        );
    }

    // Client Information
    const addressLines: string[] = [];
    if (requestPatient.patient_address_1) {
        addressLines.push(requestPatient.patient_address_1);
    }
    if (requestPatient.patient_address_2) {
        addressLines.push(requestPatient.patient_address_2);
    }
    const cityStateParts: string[] = [];
    if (requestPatient.patient_city) {
        cityStateParts.push(requestPatient.patient_city);
    }
    const stateAbbreviation = formatState(requestPatient.patient_state);
    if (stateAbbreviation) {
        cityStateParts.push(stateAbbreviation);
    }
    let cityStateZip = cityStateParts.join(', ');
    if (requestPatient.patient_zip_code) {
        cityStateZip = cityStateZip
            ? `${cityStateZip} ${requestPatient.patient_zip_code}`
            : requestPatient.patient_zip_code;
    }
    if (cityStateZip) {
        addressLines.push(cityStateZip);
    }
    const patientAddress = addressLines.length ? (
        <div>
            {addressLines.map((line, idx) => (
                <div key={idx}>{line}</div>
            ))}
        </div>
    ) : (
        ''
    );

    const primaryEmail = isCampaignForSelf
        ? requestPatient.initial_email ||
          requestPatient.patient_email ||
          patient?.person?.email ||
          ''
        : requestPatient.patient_email || patient?.person?.email || '';
    const patientMailTo = primaryEmail ? `mailto:${primaryEmail}` : '';
    const patientEmail = primaryEmail ? (
        <a href={patientMailTo} className='text-primary'>
            {primaryEmail}
        </a>
    ) : (
        ''
    );

    const formatPhone = (phone?: string, phoneType?: string | null) => {
        if (!phone) return '';
        if (!phoneType) return phone;
        return `${phone} (${phoneType})`;
    };

    const extractPhoneNumber = (phone?: string) => {
        if (!phone) return '';
        return phone.replace(/\D/g, '');
    };

    const phoneLines = isCampaignForSelf
        ? [
              formatPhone(requestPatient.initial_phone, ''),
              formatPhone(
                  requestPatient.patient_phone1,
                  requestPatient.patient_phone1_type,
              ),
              formatPhone(
                  requestPatient.patient_phone2,
                  requestPatient.patient_phone2_type,
              ),
          ].filter((value) => value)
        : [
              formatPhone(
                  requestPatient.patient_phone1,
                  requestPatient.patient_phone1_type,
              ),
              formatPhone(
                  requestPatient.patient_phone2,
                  requestPatient.patient_phone2_type,
              ),
          ].filter((value) => value);

    const seenPhones = new Set<string>();
    const uniquePhoneLines = phoneLines.filter((formattedPhone) => {
        const phoneDigits = extractPhoneNumber(formattedPhone);
        if (!phoneDigits || seenPhones.has(phoneDigits)) {
            return false;
        }
        seenPhones.add(phoneDigits);
        return true;
    });

    if (!uniquePhoneLines.length && patient?.person?.phone) {
        uniquePhoneLines.push(patient.person.phone);
    }
    const patientPhones = uniquePhoneLines.length
        ? uniquePhoneLines.map((phone, idx) => <div key={idx}>{phone}</div>)
        : '';

    const requestFirstName = isCampaignForSelf
        ? requestPatient.initial_first_name ||
          requestPatient.patient_first_name ||
          patient?.person?.first_name ||
          ''
        : requestPatient.patient_first_name ||
          patient?.person?.first_name ||
          '';
    const requestMiddleName = isCampaignForSelf
        ? requestPatient.initial_middle_name ||
          requestPatient.patient_middle_name ||
          patient?.person?.middle_name ||
          ''
        : requestPatient.patient_middle_name ||
          patient?.person?.middle_name ||
          '';
    const requestLastName = isCampaignForSelf
        ? requestPatient.initial_last_name ||
          requestPatient.patient_last_name ||
          patient?.person?.last_name ||
          ''
        : requestPatient.patient_last_name || patient?.person?.last_name || '';

    const firstNameDisplay = requestFirstName || '';
    const middleNameDisplay = requestMiddleName || '';
    const lastNameDisplay = requestLastName || '';
    const genderDisplay = requestPatient.patient_gender?.trim() || '';

    const birthDateValue =
        requestPatient.patient_birth_date || patient?.person?.birth_date;
    const birthDateDisplay = formatDate(birthDateValue);

    const medicalReason = campaignRequest?.medical_reason?.name || '';
    const primaryMedicalIssue =
        campaignRequest?.primary_medical_issue?.name || '';
    const secondaryMedicalIssue =
        campaignRequest?.secondary_medical_issue?.name || '';
    const medicalIssueNotes = campaignRequest?.medical_issue_notes || '';
    const insurance = campaignRequest?.insurance_type?.name || '';
    const injuryTimeFrame = campaignRequest?.injury_time_frame?.name || '';

    const sectionHeadingClass = 'text-[26px]/[32px] font-semibold';

    return (
        <div>
            {/* Primary Contact Section */}
            <div>
                <div className='mb-4 flex items-start justify-between'>
                    <div>
                        <h3 className={sectionHeadingClass}>Initial Contact</h3>
                        {isApproved && (
                            <div className='text-amber-600 mt-1 text-[12px]/[16px] font-medium'>
                                ⓘ Request approved - initial contact info is
                                read-only here.
                            </div>
                        )}
                    </div>
                    {!isApproved && (
                        <button
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => {
                                setIsContactDrawerOpen(true);
                            }}
                        >
                            EDIT
                        </button>
                    )}
                </div>
                <div className='flex gap-6'>
                    <div className='w-1/2'>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Name
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {[
                                campaignRequest?.initial_first_name,
                                campaignRequest?.initial_last_name,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Email
                        </div>
                        <div className='mb-4 flex items-center gap-2 text-[16px]/[24px]'>
                            {initialEmail}
                            {campaignRequest?.initial_email && (
                                <button
                                    type='button'
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            campaignRequest.initial_email,
                                        );
                                        toast.success(
                                            'Email copied to clipboard',
                                        );
                                    }}
                                    className='rounded p-1 transition-colors'
                                    style={{ color: '#00AEB3' }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            '#E6F7F8';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            'transparent';
                                    }}
                                    title='Copy email'
                                >
                                    <MdContentCopy size={16} />
                                </button>
                            )}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Phone
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {campaignRequest?.initial_phone ?? ''}
                        </div>
                    </div>
                    <div className='w-1/2'>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Request Source
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {requestSource}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Relationship to Client
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {campaignRequest?.campaign_is_for?.name}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Referred by
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {campaignRequest?.heard_of_source?.name || ''}
                            {campaignRequest?.heard_of_source_other && (
                                <span className='text-secondary'>
                                    {' '}
                                    - {campaignRequest.heard_of_source_other}
                                </span>
                            )}
                        </div>
                        {/* Show whenever the request has a medical center. The
                            referral sources that save one go well beyond
                            'med_center' (social worker, financial counselor,
                            case manager, transplant coordinator...), and legacy
                            requests carry one under other sources too. */}
                        {campaignRequest?.medical_center && (
                            <>
                                <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                    Medical Center
                                </div>
                                <div className='mb-4 text-[16px]/[24px]'>
                                    {campaignRequest.medical_center.name}
                                </div>
                            </>
                        )}
                        {referringProfessionalName(campaignRequest) && (
                            <>
                                <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                    Referring Medical Professional
                                </div>
                                <div className='mb-4 text-[16px]/[24px]'>
                                    {referringProfessionalName(campaignRequest)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className='my-6 border-t border-stroke'></div>

            {/* Client Information Section */}
            <div>
                <div className='mb-4 flex items-start justify-between'>
                    <div>
                        <h3 className={sectionHeadingClass}>Client Info</h3>
                        {isCampaignForSelf && (
                            <div className='text-blue-600 mt-1 text-[12px]/[16px] font-medium'>
                                Contact information shared from Initial Contact
                            </div>
                        )}
                        {isApproved && (
                            <div className='text-amber-600 mt-1 text-[12px]/[16px] font-medium'>
                                ⓘ Campaign approved - client info is read-only
                                here.
                            </div>
                        )}
                    </div>
                    {!isApproved && (
                        <button
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => {
                                setIsClientDrawerOpen(true);
                            }}
                        >
                            EDIT
                        </button>
                    )}
                </div>
                <div className='flex gap-6'>
                    <div className='w-1/2 min-w-0 pr-2'>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Name
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {[
                                firstNameDisplay,
                                middleNameDisplay,
                                lastNameDisplay,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Email
                        </div>
                        <div className='mb-4 flex items-center gap-2 overflow-hidden break-words text-[16px]/[24px]'>
                            {patientEmail}
                            {primaryEmail && (
                                <button
                                    type='button'
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            primaryEmail,
                                        );
                                        toast.success(
                                            'Email copied to clipboard',
                                        );
                                    }}
                                    className='flex-shrink-0 rounded p-1 transition-colors'
                                    style={{ color: '#00AEB3' }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            '#E6F7F8';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            'transparent';
                                    }}
                                    title='Copy email'
                                >
                                    <MdContentCopy size={16} />
                                </button>
                            )}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Phone
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {patientPhones}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Gender
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {genderDisplay}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Date of Birth
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {birthDateDisplay}
                        </div>
                        <div className='mb-1 text-[12px]/[16px] text-secondary'>
                            Address
                        </div>
                        <div className='mb-4 text-[16px]/[24px]'>
                            {patientAddress}
                        </div>
                    </div>
                    <div className='w-1/2 min-w-0'>
                        <div className='flex flex-col gap-4'>
                            <div>
                                <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                    Primary Medical Reason
                                </div>
                                <div className='text-[16px]/[24px]'>
                                    {medicalReason}
                                </div>
                            </div>
                            {primaryMedicalIssue && (
                                <div>
                                    <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                        Primary Medical Issue
                                    </div>
                                    <div className='text-[16px]/[24px]'>
                                        {primaryMedicalIssue}
                                    </div>
                                </div>
                            )}
                            {medicalIssueNotes && (
                                <div>
                                    <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                        Primary Medical Notes
                                    </div>
                                    <div className='whitespace-pre-wrap text-[16px]/[24px]'>
                                        {medicalIssueNotes}
                                    </div>
                                </div>
                            )}
                            {secondaryMedicalIssue && (
                                <div>
                                    <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                        Secondary Medical Issue
                                    </div>
                                    <div className='text-[16px]/[24px]'>
                                        {secondaryMedicalIssue}
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                    {medicalReason
                                        ? `${medicalReason} Timeframe`
                                        : 'Timeframe'}
                                </div>
                                <div className='text-[16px]/[24px]'>
                                    {injuryTimeFrame}
                                </div>
                            </div>
                            <div>
                                <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                    Insurance
                                </div>
                                <div className='text-[16px]/[24px]'>
                                    {insurance}
                                </div>
                            </div>
                            <div>
                                <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                    Community
                                </div>
                                <ul className='text-[16px]/[24px]'>
                                    {communityTypes}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className='my-6 border-t border-stroke'></div>

            {/* Primary Contact Section */}
            <div>
                <div className='mb-4 flex items-start justify-between'>
                    <div>
                        <h3 className={sectionHeadingClass}>Primary Contact</h3>
                        {isApproved && (
                            <div className='text-amber-600 mt-1 text-[12px]/[16px] font-medium'>
                                ⓘ Campaign approved - primary contact info is
                                read-only here.
                            </div>
                        )}
                    </div>
                    {!isApproved && (
                        <button
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => setIsPrimaryContactDrawerOpen(true)}
                        >
                            EDIT
                        </button>
                    )}
                </div>
                {campaignRequest?.primary_relationship_to_client?.code ===
                'self' ? (
                    <div className='text-blue-600 mt-1 text-[12px]/[16px] font-medium'>
                        Primary Contact information is same as Client
                    </div>
                ) : !(
                      campaignRequest?.primary_first_name ||
                      campaignRequest?.primary_last_name ||
                      campaignRequest?.primary_email ||
                      campaignRequest?.primary_phone
                  ) ? (
                    <div className='text-[14px] text-secondary'>
                        No primary contact information has been added yet.
                    </div>
                ) : (
                    <div className='flex gap-6'>
                        <div className='w-1/2'>
                            <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                Name
                            </div>
                            <div className='mb-4 text-[16px]/[24px]'>
                                {[
                                    campaignRequest?.primary_first_name,
                                    campaignRequest?.primary_middle_name,
                                    campaignRequest?.primary_last_name,
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            </div>
                            <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                Email
                            </div>
                            <div className='mb-4 flex items-center gap-2 text-[16px]/[24px]'>
                                {campaignRequest?.primary_email ? (
                                    <>
                                        <a
                                            className='text-primary'
                                            href={`mailto:${campaignRequest.primary_email}`}
                                        >
                                            {campaignRequest.primary_email}
                                        </a>
                                        <button
                                            type='button'
                                            onClick={() => {
                                                navigator.clipboard.writeText(
                                                    campaignRequest.primary_email,
                                                );
                                                toast.success(
                                                    'Email copied to clipboard',
                                                );
                                            }}
                                            className='rounded p-1 transition-colors'
                                            style={{ color: '#00AEB3' }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    '#E6F7F8';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    'transparent';
                                            }}
                                            title='Copy email'
                                        >
                                            <MdContentCopy size={16} />
                                        </button>
                                    </>
                                ) : (
                                    ''
                                )}
                            </div>
                            <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                Contact Number
                            </div>
                            <div className='mb-4 text-[16px]/[24px]'>
                                {campaignRequest?.primary_phone || ''}
                            </div>
                            <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                Address
                            </div>
                            <div className='mb-4 text-[16px]/[24px]'>
                                {(() => {
                                    const lines: string[] = [];
                                    if (campaignRequest?.primary_address_1)
                                        lines.push(
                                            campaignRequest.primary_address_1,
                                        );
                                    if (campaignRequest?.primary_address_2)
                                        lines.push(
                                            campaignRequest.primary_address_2,
                                        );
                                    const cityState: string[] = [];
                                    if (campaignRequest?.primary_city)
                                        cityState.push(
                                            campaignRequest.primary_city,
                                        );
                                    const st =
                                        campaignRequest?.primary_state?.state ||
                                        campaignRequest?.primary_state
                                            ?.abbreviation ||
                                        '';
                                    if (st) cityState.push(st);
                                    let csz = cityState.join(', ');
                                    if (campaignRequest?.primary_zip_code)
                                        csz = csz
                                            ? `${csz} ${campaignRequest.primary_zip_code}`
                                            : campaignRequest.primary_zip_code;
                                    if (csz) lines.push(csz);
                                    return lines.length
                                        ? lines.map((line, idx) => (
                                              <div key={idx}>{line}</div>
                                          ))
                                        : '';
                                })()}
                            </div>
                        </div>
                        <div className='w-1/2'>
                            <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                Relationship to Client
                            </div>
                            <div className='mb-4 text-[16px]/[24px]'>
                                {campaignRequest?.primary_relationship_to_client
                                    ?.name || ''}
                            </div>
                            {campaignRequest?.primary_is_emergency_contact && (
                                <div className='mb-4'>
                                    <div className='mb-1 text-[12px]/[16px] text-secondary'>
                                        Emergency Contact
                                    </div>
                                    <div className='text-amber-600 text-[16px]/[24px] font-medium'>
                                        Yes
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Drawers */}
            <InitialContactInfoDrawer
                campaignRequest={campaignRequest}
                metadata={metadata}
                isOpen={isContactDrawerOpen}
                setIsOpen={setIsContactDrawerOpen}
                onUpdate={onUpdate}
            />
            <PrimaryContactInfoDrawer
                campaignRequest={campaignRequest}
                metadata={metadata}
                isOpen={isPrimaryContactDrawerOpen}
                setIsOpen={setIsPrimaryContactDrawerOpen}
                onUpdate={onUpdate}
            />
            <CampaignRequestClientInfoDrawer
                campaignRequest={campaignRequest}
                person={patient?.person}
                application={application}
                isOpen={isClientDrawerOpen}
                setIsOpen={setIsClientDrawerOpen}
                states={metadata?.states}
                medicalReasons={metadata?.medical_reasons}
                medicalIssues={metadata?.medical_issues}
                injuryTimeFrames={metadata?.injury_time_frames}
                insuranceTypes={metadata?.insurance_types}
                transplantDiagnoses={metadata?.transplant_diagnoses}
                candidacyStatuses={metadata?.candidacy_statuses}
                communityTypes={metadata?.community_types}
                onPatientUpdate={onUpdate}
            />
        </div>
    );
};

export default RequestOverview;
